import * as THREE from 'three';
import { Line, Mesh, TorusGeometry } from 'three';

import { Gizmo, GizmoLineMaterial, pickerMaterial } from './gizmo';

function createCircleGeometry(radius: number, aroundAxis: 'x' | 'y' | 'z', arc: 1 | 0.5) {
  const geometry = new THREE.BufferGeometry();
  const vertices = [];

  for (let i = 0; i <= 64 * arc; ++i) {
    if (aroundAxis === 'x') {
      vertices.push(
        0,
        Math.cos((i / 32) * Math.PI) * radius,
        Math.sin((i / 32) * Math.PI) * radius,
      );
    }
    if (aroundAxis === 'y') {
      vertices.push(
        Math.cos((i / 32) * Math.PI) * radius,
        0,
        Math.sin((i / 32) * Math.PI) * radius,
      );
    }
    if (aroundAxis === 'z') {
      vertices.push(
        Math.sin((i / 32) * Math.PI) * radius,
        Math.cos((i / 32) * Math.PI) * radius,
        0,
      );
    }
  }

  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  return geometry;
}

export class GizmoRotate extends Gizmo {
  constructor() {
    super();

    this.handleGizmos = {
      X: [[new Line(createCircleGeometry(1, 'x', 1), new GizmoLineMaterial({ color: 0xff0000 }))]],

      Y: [[new Line(createCircleGeometry(1, 'y', 1), new GizmoLineMaterial({ color: 0x00ff00 }))]],

      Z: [[new Line(createCircleGeometry(1, 'z', 1), new GizmoLineMaterial({ color: 0x0000ff }))]],
    };

    this.pickerGizmos = {
      X: [
        [
          new Mesh(new TorusGeometry(1, 0.12, 4, 12), pickerMaterial),
          [0, 0, 0],
          [0, -Math.PI / 2, -Math.PI / 2],
        ],
      ],

      Y: [
        [
          new Mesh(new TorusGeometry(1, 0.12, 4, 12), pickerMaterial),
          [0, 0, 0],
          [Math.PI / 2, 0, 0],
        ],
      ],

      Z: [
        [
          new Mesh(new TorusGeometry(1, 0.12, 4, 12), pickerMaterial),
          [0, 0, 0],
          [0, 0, -Math.PI / 2],
        ],
      ],
    };

    this.init();
  }

  setActivePlane(axis: any) {
    if (axis === 'X') {
      this.activePlane = this.planes!.YZ;
    }

    if (axis === 'Y') {
      this.activePlane = this.planes!.XZ;
    }

    if (axis === 'Z') {
      this.activePlane = this.planes!.XY;
    }
  }

  update(rotation: THREE.Euler, cameraDirection: THREE.Vector3) {
    super.update(rotation, cameraDirection);

    const worldRotation = new THREE.Euler(0, 0, 1);
    const tempQuaternion = new THREE.Quaternion();
    const direction = cameraDirection.clone();

    worldRotation.copy(this.planes!.XY!.rotation);
    tempQuaternion.setFromEuler(worldRotation);

    direction.applyMatrix4(
      new THREE.Matrix4().extractRotation(this.planes!.XY!.matrixWorld).invert(),
    );

    this.traverse((child) => {
      tempQuaternion.setFromEuler(worldRotation);

      if (child.name === 'X') {
        const q = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          Math.atan2(-direction.y, direction.z),
        );
        tempQuaternion.multiplyQuaternions(tempQuaternion, q);
        child.quaternion.copy(tempQuaternion);
      }

      if (child.name === 'Y') {
        const q = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          Math.atan2(direction.x, direction.z),
        );
        tempQuaternion.multiplyQuaternions(tempQuaternion, q);
        child.quaternion.copy(tempQuaternion);
      }

      if (child.name === 'Z') {
        const q = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          Math.atan2(direction.y, direction.x),
        );
        tempQuaternion.multiplyQuaternions(tempQuaternion, q);
        child.quaternion.copy(tempQuaternion);
      }
    });
  }
}
